// Employee pay terms; same rules as the mobile app and the server.
import type { CommissionMode, PayBaseType, PayTerms } from "../../services/settingsApi";
import { formatMoney } from "../../services/currency";

const BASE_TYPES: PayBaseType[] = ["none", "hourly", "per_event"];
const COMMISSION_MODES: CommissionMode[] = ["add", "instead", "higher"];

const nonNegative = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

/** Employees saved before pay settings only have a commission percent. */
export const normalizePayTerms = (
  raw?: Partial<PayTerms> | null,
  legacyCommissionPercent?: number
): PayTerms => {
  const baseType = BASE_TYPES.includes(raw?.baseType as PayBaseType)
    ? (raw!.baseType as PayBaseType)
    : "none";
  return {
    baseType,
    baseAmount: baseType === "none" ? 0 : nonNegative(raw?.baseAmount),
    commissionPercent: Math.min(
      100,
      nonNegative(raw?.commissionPercent ?? legacyCommissionPercent)
    ),
    commissionThreshold: nonNegative(raw?.commissionThreshold),
    commissionMode: COMMISSION_MODES.includes(raw?.commissionMode as CommissionMode)
      ? (raw!.commissionMode as CommissionMode)
      : "add",
  };
};

const money = (value: number) => {
  const text = formatMoney(value);
  return text.endsWith(".00") ? text.slice(0, -3) : text;
};

/** "$100 per event, or 15% commission instead once sales reach $500". */
export const describePay = (terms: PayTerms): string => {
  const base =
    terms.baseType === "hourly"
      ? `${money(terms.baseAmount)}/hour`
      : terms.baseType === "per_event"
        ? `${money(terms.baseAmount)} per event`
        : "";
  if (terms.commissionPercent <= 0) return base || "No pay set";

  const minimum =
    terms.commissionThreshold > 0
      ? ` once sales reach ${money(terms.commissionThreshold)}`
      : "";
  const commission = `${Number(terms.commissionPercent.toFixed(2))}% commission`;
  if (!base) return `${commission}${minimum}`;
  if (terms.commissionMode === "higher") {
    return `${base} or ${commission}, whichever is higher${minimum}`;
  }
  return terms.commissionMode === "instead"
    ? `${base}, or ${commission} instead${minimum}`
    : `${base} + ${commission}${minimum}`;
};

/** Text-field version of PayTerms while the owner is typing. */
export type PayTermsDraft = {
  baseType: PayBaseType;
  baseAmount: string;
  commissionPercent: string;
  commissionThreshold: string;
  commissionMode: CommissionMode;
};

const asText = (value: number) => (value ? String(value) : "");

export const draftFromTerms = (terms: PayTerms): PayTermsDraft => ({
  baseType: terms.baseType,
  baseAmount: asText(terms.baseAmount),
  commissionPercent: asText(terms.commissionPercent),
  commissionThreshold: asText(terms.commissionThreshold),
  commissionMode: terms.commissionMode,
});

const parseMoney = (text: string) => {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return /^\d+(\.\d{0,2})?$/.test(trimmed) ? Number(trimmed) : NaN;
};

/** Same rules as the server; returns a message to show on bad input. */
export const termsFromDraft = (
  draft: PayTermsDraft
): { terms: PayTerms } | { error: string } => {
  const baseAmount = draft.baseType === "none" ? 0 : parseMoney(draft.baseAmount);
  const commissionPercent = parseMoney(draft.commissionPercent);
  const commissionThreshold =
    commissionPercent > 0 ? parseMoney(draft.commissionThreshold) : 0;

  if (Number.isNaN(baseAmount) || baseAmount > 100000) {
    return { error: "Enter a valid pay amount." };
  }
  if (draft.baseType !== "none" && baseAmount <= 0) {
    return {
      error:
        draft.baseType === "hourly"
          ? "Enter an hourly rate above 0."
          : "Enter the pay per event above 0.",
    };
  }
  if (Number.isNaN(commissionPercent) || commissionPercent > 100) {
    return { error: "Commission must be between 0 and 100%." };
  }
  if (Number.isNaN(commissionThreshold)) {
    return { error: "Enter a valid sales amount." };
  }
  return {
    terms: {
      baseType: draft.baseType,
      baseAmount,
      commissionPercent,
      commissionThreshold,
      // Only matters when there is both a base and a commission.
      commissionMode:
        draft.baseType !== "none" && commissionPercent > 0 ? draft.commissionMode : "add",
    },
  };
};
